import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { 
  Dumbbell, 
  Heart, 
  Target, 
  Zap, 
  Activity, 
  Smile, 
  Clock, 
  MapPin, 
  Phone, 
  Mail,
  Check,
  Star,
  Menu,
  X,
  Award,
  Users,
  TrendingUp,
  ChevronRight,
  Sparkles,
  MessageCircle,
  Trophy,
  Gift,
  Calendar,
  DoorOpen,
  Apple
} from 'lucide-react';
import { 
  gymInfo, 
  testimonials, 
  trainingSpaces, 
  benefits, 
  objectives, 
  pricingPlans,
  creneaux,
  coaches,
  apprentices,
  faqItems,
  mission,
  coachSchedule,
  transformations,
  liveStats,
  referralProgram,
  upcomingEvents
} from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  target: Target,
  dumbbell: Dumbbell,
  heart: Heart,
  zap: Zap,
  activity: Activity,
  smile: Smile
};

export default function Home() {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    objectif: '',
    creneau: '',
    message: ''
  });
  const [coachingFormData, setCoachingFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    coach: '',
    jour: '',
    horaire: '',
    date: ''
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingCoaching, setIsSubmittingCoaching] = useState(false);
  const [showExitPopup, setShowExitPopup] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.status === 'success') {
        toast.success(response.data.message);
        setFormData({
          nom: '',
          prenom: '',
          email: '',
          telephone: '',
          objectif: '',
          creneau: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error.response?.data?.detail || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCoachingChange = (e) => {
    setCoachingFormData({
      ...coachingFormData,
      [e.target.name]: e.target.value
    });
  };

  const handleCoachingSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingCoaching(true);
    
    try {
      const response = await axios.post(`${API}/coaching-booking`, coachingFormData);
      
      if (response.data.status === 'success') {
        toast.success(response.data.message);
        setCoachingFormData({
          nom: '',
          prenom: '',
          email: '',
          telephone: '',
          coach: '',
          jour: '',
          horaire: '',
          date: ''
        });
      }
    } catch (error) {
      console.error('Error submitting coaching form:', error);
      toast.error(error.response?.data?.detail || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmittingCoaching(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  // Détection de l'intention de sortie pour le pop-up
  React.useEffect(() => {
    let exitIntentShown = false;

    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !exitIntentShown && !showExitPopup) {
        exitIntentShown = true;
        setShowExitPopup(true);
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [showExitPopup]);

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header Sticky */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-header">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <img src="/assets/logo-vita-liberte.png" alt="Vita Liberté" className="h-10 w-10" />
              <span className="text-xl font-bold text-white">Vita Liberté</span>
            </div>
            
            <nav className="hidden md:flex items-center space-x-6">
              <button onClick={() => scrollToSection('presentation')} className="nav-link">La Salle</button>
              <button onClick={() => scrollToSection('tarifs')} className="nav-link">Tarifs</button>
              <button onClick={() => scrollToSection('temoignages')} className="nav-link">Avis</button>
              <button onClick={() => scrollToSection('contact')} className="nav-link">Contact</button>
              <Button onClick={() => scrollToSection('essai-gratuit')} className="btn-primary pulse-animation">
                Séance Gratuite
              </Button>
            </nav>

            <button 
              className="md:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 flex flex-col space-y-3">
              <button onClick={() => scrollToSection('presentation')} className="nav-link text-left">La Salle</button>
              <button onClick={() => scrollToSection('tarifs')} className="nav-link text-left">Tarifs</button>
              <button onClick={() => scrollToSection('temoignages')} className="nav-link text-left">Avis</button>
              <button onClick={() => scrollToSection('contact')} className="nav-link text-left">Contact</button>
              <Button onClick={() => scrollToSection('essai-gratuit')} className="btn-primary w-full">
                Séance Gratuite
              </Button>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section - Optimisé SEO Local */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_musculation-fitness/artifacts/x94kigce_Vita-liberte%CC%81-bagnols-sur-ceze-exterieur.jpg"
            alt="Salle de sport Bagnols-sur-Cèze"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-950/70 to-slate-950"></div>
        </div>

        <div className="container mx-auto px-4 z-10 text-center">
          <div className="animate-fade-in-up">
            <div className="inline-block mb-4">
              <span className="bg-gradient-to-r from-pink-500 to-cyan-500 text-white px-4 py-2 rounded-full text-sm font-bold">
                ⭐ Séance d'essai 100% GRATUITE
              </span>
            </div>
            <h1 className="hero-title mb-6">
              Salle de Sport à <span className="text-gradient">Bagnols-sur-Cèze</span><br />
              Transformez Votre Corps Dès Aujourd'hui
            </h1>
            <p className="hero-subtitle mb-4">
              Musculation • Cardio • Coaching personnalisé • Nutrition<br />
              Accès 7j/7 de 6h à 23h • Équipements professionnels • Ambiance motivante
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <Button onClick={() => scrollToSection('essai-gratuit')} size="lg" className="btn-primary-large pulse-animation text-xl">
                <Sparkles className="mr-2 h-6 w-6" />
                Réserver Ma Séance GRATUITE
              </Button>
              <Button onClick={() => scrollToSection('presentation')} size="lg" variant="outline" className="btn-secondary-large text-xl">
                Découvrir La Salle
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center text-white">
                <Check className="h-5 w-5 text-cyan-400 mr-2" />
                <span>Sans engagement</span>
              </div>
              <div className="flex items-center text-white">
                <Check className="h-5 w-5 text-cyan-400 mr-2" />
                <span>Coaching inclus</span>
              </div>
              <div className="flex items-center text-white">
                <Check className="h-5 w-5 text-cyan-400 mr-2" />
                <span>Résultats garantis</span>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-pink-400 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-pink-400 rounded-full mt-2"></div>
          </div>
        </div>
      </section>

      {/* Présentation Rapide */}
      <section id="presentation" className="section-padding bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <div className="inline-block mb-4">
              <span className="text-pink-400 font-semibold text-sm uppercase tracking-wider">Bienvenue chez Vita Liberté</span>
            </div>
            <h2 className="section-title mb-6">La Meilleure Salle de Musculation à Bagnols-sur-Cèze</h2>
            <p className="text-xl text-slate-300 leading-relaxed">
              Depuis notre ouverture, nous avons aidé des centaines de personnes à atteindre leurs objectifs fitness. 
              Que vous souhaitiez <strong className="text-white">perdre du poids</strong>, <strong className="text-white">prendre du muscle</strong> 
              ou simplement <strong className="text-white">vous remettre en forme</strong>, notre équipe de coachs professionnels est là pour vous accompagner à chaque étape.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center animate-scale-in" style={{animationDelay: '0.1s'}}>
              <div className="w-20 h-20 bg-gradient-to-br from-pink-500 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-4xl font-bold text-white mb-2">{liveStats.members}+</h3>
              <p className="text-slate-300 font-semibold">Membres actifs</p>
            </div>
            <div className="text-center animate-scale-in" style={{animationDelay: '0.2s'}}>
              <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-4xl font-bold text-white mb-2">{liveStats.transformations}+</h3>
              <p className="text-slate-300 font-semibold">Transformations réussies</p>
            </div>
            <div className="text-center animate-scale-in" style={{animationDelay: '0.3s'}}>
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-4xl font-bold text-white mb-2">{liveStats.satisfaction}%</h3>
              <p className="text-slate-300 font-semibold">Satisfaction clients</p>
            </div>
          </div>
        </div>
      </section>

      {/* Photos immersives de la salle */}
      <section id="espaces" className="section-padding bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Découvrez Nos Espaces d'Entraînement</h2>
            <p className="section-description mt-4">
              Des équipements professionnels pour tous vos objectifs fitness
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {trainingSpaces.map((space, index) => (
              <Card key={space.id} className="training-card animate-scale-in overflow-hidden" style={{animationDelay: `${index * 0.15}s`}}>
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={space.image} 
                    alt={space.title}
                    className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950 to-transparent"></div>
                </div>
                <div className="p-6 bg-slate-900">
                  <h3 className="text-2xl font-bold text-white mb-2">{space.title}</h3>
                  <p className="text-slate-200 text-base font-medium leading-relaxed">
                    {space.description}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Avantages */}
      <section className="section-padding bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Pourquoi Choisir Vita Liberté Bagnols-sur-Cèze ?</h2>
            <p className="section-description mt-4">
              Tout ce dont vous avez besoin pour réussir votre transformation
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="feature-card animate-scale-in" style={{animationDelay: `${index * 0.1}s`}}>
                <Check className="h-6 w-6 text-cyan-400 mb-2" />
                <p className="text-white font-medium">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Intermédiaire 1 */}
      <section className="py-16 bg-gradient-to-r from-pink-600 via-purple-600 to-cyan-600">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Prêt à Commencer Votre Transformation ?
          </h3>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Réservez votre séance d'essai gratuite maintenant et découvrez pourquoi nos membres adorent s'entraîner chez nous
          </p>
          <Button onClick={() => scrollToSection('essai-gratuit')} size="lg" className="bg-white text-purple-600 hover:bg-slate-100 font-bold text-lg px-8 py-6">
            Je Réserve Ma Séance Gratuite
            <ChevronRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Objectifs */}
      <section className="section-padding bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Quel Est Votre Objectif Fitness ?</h2>
            <p className="section-description mt-4">
              Nos programmes sont adaptés à tous vos besoins
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
            {objectives.map((obj, index) => {
              const IconComponent = iconMap[obj.icon];
              return (
                <div key={obj.id} className="objective-card animate-scale-in" style={{animationDelay: `${index * 0.1}s`}}>
                  <IconComponent className="h-8 w-8 text-pink-400 mb-3" />
                  <h3 className="text-lg font-semibold text-white">{obj.title}</h3>
                </div>
              );
            })}
          </div>

          <div className="text-center">
            <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
              Peu importe votre niveau actuel ou votre objectif, nos coachs créent un programme 100% personnalisé pour vous.
            </p>
            <Button onClick={() => scrollToSection('essai-gratuit')} size="lg" className="btn-primary-large">
              Commencer Mon Programme Personnalisé
            </Button>
          </div>
        </div>
      </section>

      {/* Mission / Storytelling */}
      <section className="section-padding bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="section-title mb-6">{mission.title}</h2>
            <p className="text-xl text-slate-300 leading-relaxed mb-8">
              {mission.description}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {mission.values.map((value) => (
              <Card key={value.id} className="bg-slate-800/60 border-slate-700 hover:border-pink-500/50 transition-all duration-300">
                <CardContent className="pt-6 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Heart className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{value.title}</h3>
                  <p className="text-slate-300">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Coachs */}
      <section className="section-padding bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="section-title">Notre Équipe de Coachs</h2>
            <p className="section-description mt-4">
              Des professionnels passionnés pour vous accompagner vers vos objectifs
            </p>
          </div>

          {/* Coach Principal */}
          <div className="max-w-4xl mx-auto">
            {coaches.map((coach) => (
              <Card key={coach.id} className="bg-gradient-to-br from-pink-900/30 to-cyan-900/30 border-2 border-pink-500/50 overflow-hidden">
                <CardContent className="p-8 md:p-12">
                  <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
                    {coach.image ? (
                      <div className="w-48 h-48 rounded-2xl overflow-hidden border-4 border-pink-500/50 flex-shrink-0 shadow-2xl">
                        <img 
                          src={coach.image} 
                          alt={coach.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-48 h-48 bg-gradient-to-br from-pink-500 to-cyan-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <Dumbbell className="h-20 w-20 text-white" />
                      </div>
                    )}
                    <div className="flex-1 text-center md:text-left">
                      <h3 className="text-3xl md:text-4xl font-bold text-white mb-2">{coach.name}</h3>
                      <p className="text-pink-400 font-bold text-xl mb-4">{coach.speciality}</p>
                      <p className="text-slate-200 text-lg leading-relaxed">{coach.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Intermédiaire 2 */}
      <section className="py-16 bg-gradient-to-r from-cyan-600 via-purple-600 to-pink-600">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Venez Rencontrer Nos Coachs
          </h3>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Profitez de votre séance gratuite pour échanger avec l'équipe et découvrir comment nous pouvons vous aider
          </p>
          <Button onClick={() => scrollToSection('essai-gratuit')} size="lg" className="bg-white text-purple-600 hover:bg-slate-100 font-bold text-lg px-8 py-6">
            Réserver Ma Visite Gratuite
            <ChevronRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Témoignages */}
      <section id="temoignages" className="section-padding bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Ils Ont Transformé Leur Corps Chez Nous</h2>
            <p className="section-description mt-4">
              Rejoignez des centaines de membres satisfaits
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <Card key={testimonial.id} className="testimonial-card animate-scale-in" style={{animationDelay: `${index * 0.15}s`}}>
                <CardContent className="pt-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-slate-300 mb-4 italic text-lg">"{testimonial.text}"</p>
                  <p className="text-pink-400 font-semibold">— {testimonial.name}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <div className="inline-block bg-gradient-to-r from-pink-500/20 to-cyan-500/20 border border-cyan-500/30 rounded-lg p-6">
              <p className="text-white text-lg mb-2">
                <strong className="text-cyan-400">98% de nos membres</strong> recommandent Vita Liberté à leurs proches
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Transformations */}
      <section className="section-padding bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="section-title">Leurs Transformations Inspirantes</h2>
            <p className="section-description mt-4">
              Des résultats concrets, des objectifs atteints
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {transformations.map((transformation, index) => (
              <Card key={transformation.id} className="bg-gradient-to-br from-slate-900 to-slate-800 border-cyan-500/30 overflow-hidden hover:border-pink-500/50 transition-all duration-300 animate-scale-in" style={{animationDelay: `${index * 0.1}s`}}>
                <CardContent className="p-8">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-2xl font-bold text-white">{transformation.name}</h3>
                      <p className="text-slate-400 text-sm">{transformation.program}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-cyan-400">
                        {transformation.result}
                      </div>
                    </div>
                  </div>
                  <p className="text-slate-300 italic mb-4">"{transformation.testimonial}"</p>
                  <div className="flex items-center text-cyan-400">
                    <Check className="h-5 w-5 mr-2" />
                    <span className="text-sm font-semibold">Objectif atteint</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button onClick={() => scrollToSection('essai-gratuit')} size="lg" className="btn-primary-large">
              Je Veux Le Même Résultat !
            </Button>
          </div>
        </div>
      </section>

      {/* Tarifs */}
      <section id="tarifs" className="section-padding bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Choisissez Votre Formule</h2>
            <p className="section-description mt-4">
              Des tarifs transparents adaptés à tous les budgets
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <Card 
                key={plan.id} 
                className={`pricing-card animate-scale-in ${plan.highlight ? 'pricing-card-highlight ring-2 ring-pink-500' : ''}`}
                style={{animationDelay: `${index * 0.15}s`}}
              >
                <CardContent className="p-8">
                  {plan.highlight && (
                    <div className="mb-4 text-center">
                      <span className="inline-block bg-pink-500 text-white px-4 py-2 rounded-full text-sm font-bold">
                        ⭐ Le Plus Populaire
                      </span>
                    </div>
                  )}
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                    <div className="mt-4">
                      <span className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-cyan-500">{plan.price}</span>
                      <span className="text-slate-700 ml-2 font-semibold">{plan.period}</span>
                    </div>
                    <p className="text-slate-800 mt-3 font-medium text-lg">
                      {plan.description}
                    </p>
                    {plan.note && (
                      <p className="text-sm text-slate-600 mt-2 italic font-medium">* {plan.note}</p>
                    )}
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <Check className="h-5 w-5 text-cyan-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-900 font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    onClick={() => scrollToSection('essai-gratuit')} 
                    className={plan.highlight ? 'btn-primary w-full text-lg py-6' : 'btn-secondary w-full text-lg py-6'}
                  >
                    Essayer Gratuitement
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-slate-300 text-lg mb-2">
              💡 <span className="font-semibold text-white">D'autres formules sont à découvrir au club directement</span>
            </p>
            <p className="text-slate-400">
              Notre équipe vous présentera toutes les options adaptées à vos besoins
            </p>
          </div>
        </div>
      </section>

      {/* PRISE DE RENDEZ-VOUS COACHING - FORFAIT FULL OU SUIVI */}
      <section id="rdv-coaching" className="section-padding bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-block mb-4">
                <span className="bg-gradient-to-r from-pink-500 to-cyan-500 text-white px-6 py-2 rounded-full text-sm font-bold uppercase">
                  Réservé aux membres Full & Suivi
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Prise de Rendez-vous<br/>
                <span className="text-gradient">Pour Votre Séance de Coaching</span>
              </h2>
              <p className="text-xl text-slate-300">
                Membres Full et Suivi : Réservez votre séance avec votre coach
              </p>
            </div>

            <Card className="glass-card p-8">
              <form onSubmit={handleCoachingSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="coaching-nom" className="text-white font-semibold">Nom *</Label>
                    <Input
                      id="coaching-nom"
                      name="nom"
                      value={coachingFormData.nom}
                      onChange={handleCoachingChange}
                      required
                      className="input-field border-2 border-slate-700"
                      placeholder="Votre nom"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="coaching-prenom" className="text-white font-semibold">Prénom *</Label>
                    <Input
                      id="coaching-prenom"
                      name="prenom"
                      value={coachingFormData.prenom}
                      onChange={handleCoachingChange}
                      required
                      className="input-field border-2 border-slate-700"
                      placeholder="Votre prénom"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="coaching-email" className="text-white font-semibold">Email *</Label>
                    <Input
                      id="coaching-email"
                      name="email"
                      type="email"
                      value={coachingFormData.email}
                      onChange={handleCoachingChange}
                      required
                      className="input-field border-2 border-slate-700"
                      placeholder="votre@email.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="coaching-telephone" className="text-white font-semibold">Téléphone *</Label>
                    <Input
                      id="coaching-telephone"
                      name="telephone"
                      type="tel"
                      value={coachingFormData.telephone}
                      onChange={handleCoachingChange}
                      required
                      className="input-field border-2 border-slate-700"
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="coaching-coach" className="text-white font-semibold">Coach *</Label>
                  <select
                    id="coaching-coach"
                    name="coach"
                    value={coachingFormData.coach}
                    onChange={handleCoachingChange}
                    required
                    className="input-field w-full border-2 border-slate-700"
                  >
                    <option value="">Sélectionnez un coach</option>
                    <option value="MAXIME">MAXIME DOUARD</option>
                  </select>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="coaching-jour" className="text-white font-semibold">Jour souhaité *</Label>
                    <select
                      id="coaching-jour"
                      name="jour"
                      value={coachingFormData.jour}
                      onChange={handleCoachingChange}
                      required
                      className="input-field w-full border-2 border-slate-700"
                    >
                      <option value="">Sélectionnez un jour</option>
                      <option value="Lundi">Lundi</option>
                      <option value="Mardi">Mardi</option>
                      <option value="Mercredi">Mercredi</option>
                      <option value="Jeudi">Jeudi</option>
                      <option value="Vendredi">Vendredi</option>
                      <option value="Samedi">Samedi</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="coaching-date" className="text-white font-semibold">Date *</Label>
                    <Input
                      id="coaching-date"
                      name="date"
                      type="date"
                      value={coachingFormData.date}
                      onChange={handleCoachingChange}
                      required
                      className="input-field border-2 border-slate-700"
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="coaching-horaire" className="text-white font-semibold">Horaire souhaité *</Label>
                  <select
                    id="coaching-horaire"
                    name="horaire"
                    value={coachingFormData.horaire}
                    onChange={handleCoachingChange}
                    required
                    className="input-field w-full border-2 border-slate-700"
                  >
                    <option value="">Sélectionnez un horaire</option>
                    <option value="9h-10h">9h-10h</option>
                    <option value="10h-11h">10h-11h</option>
                    <option value="11h-12h">11h-12h</option>
                    <option value="15h-16h">15h-16h</option>
                    <option value="16h-17h">16h-17h</option>
                    <option value="17h-18h">17h-18h</option>
                  </select>
                </div>

                <div className="bg-cyan-900/30 border border-cyan-500/50 rounded-lg p-4">
                  <p className="text-cyan-300 text-sm">
                    <strong>📌 Rappel :</strong> Cette demande est réservée aux membres avec forfait <strong>Full</strong> ou <strong>Suivi</strong>. Nous vous confirmerons la disponibilité par email.
                  </p>
                </div>

                <Button 
                  type="submit" 
                  size="lg"
                  className="w-full btn-primary text-lg py-6"
                  disabled={isSubmittingCoaching}
                >
                  {isSubmittingCoaching ? 'Envoi en cours...' : 'Envoyer Ma Demande de Rendez-vous'}
                </Button>
              </form>
            </Card>
          </div>
        </div>
      </section>

      {/* Programme de Parrainage */}
      <section id="parrainage" className="section-padding bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Gift className="h-16 w-16 text-pink-400 mx-auto mb-4" />
              <h2 className="section-title">{referralProgram.title}</h2>
              <p className="section-description mt-4">
                {referralProgram.description}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="bg-slate-800/90 border-2 border-pink-500/30 shadow-xl">
                <CardContent className="pt-6">
                  <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
                    <Trophy className="mr-2 text-cyan-400" />
                    Vos Avantages
                  </h3>
                  <ul className="space-y-3">
                    {referralProgram.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start text-slate-100">
                        <Check className="h-5 w-5 text-pink-400 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="font-medium">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/90 border-2 border-cyan-500/30 shadow-xl">
                <CardContent className="pt-6">
                  <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
                    <Users className="mr-2 text-pink-400" />
                    Comment Ça Marche ?
                  </h3>
                  <div className="space-y-4">
                    {referralProgram.howItWorks.map((step, idx) => (
                      <div key={idx} className="flex items-start">
                        <div className="w-8 h-8 bg-gradient-to-br from-pink-500 to-cyan-500 rounded-full flex items-center justify-center flex-shrink-0 mr-3">
                          <span className="text-white font-bold">{idx + 1}</span>
                        </div>
                        <p className="text-slate-100 mt-1 font-medium">{step}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="text-center bg-gradient-to-r from-pink-900/30 to-cyan-900/30 border-2 border-pink-500/50 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-white mb-4">Commencez à Parrainer Maintenant !</h3>
              <p className="text-slate-200 mb-6">Parlez-en à vos proches et profitez de nos avantages</p>
              <Button onClick={() => scrollToSection('contact')} size="lg" className="btn-primary-large">
                En Savoir Plus
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION DÉDIÉE SÉANCE D'ESSAI GRATUITE - CONVERSION */}
      <section id="essai-gratuit" className="section-padding bg-gradient-to-br from-pink-600 via-purple-600 to-cyan-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-block mb-4">
                <span className="bg-white text-purple-600 px-6 py-2 rounded-full text-sm font-bold uppercase">
                  Offre Limitée
                </span>
              </div>
              <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
                Votre Première Séance<br />
                <span className="text-yellow-300">100% GRATUITE</span>
              </h2>
              <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
                Testez nos équipements, rencontrez nos coachs et découvrez l'ambiance Vita Liberté.<br />
                <strong>Sans engagement. Sans frais cachés.</strong>
              </p>
            </div>

            <Card className="glass-card p-8 md:p-12 bg-white/95 backdrop-blur-xl">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">
                    Réservez Votre Séance d'Essai Gratuite
                  </h3>
                  <p className="text-slate-600">
                    Remplissez le formulaire et nous vous recontacterons sous 24h
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="nom" className="text-slate-900 font-semibold">Nom *</Label>
                    <Input
                      id="nom"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      required
                      className="input-field border-2 border-slate-300 focus:border-pink-500"
                      placeholder="Votre nom"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="prenom" className="text-slate-900 font-semibold">Prénom *</Label>
                    <Input
                      id="prenom"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleChange}
                      required
                      className="input-field border-2 border-slate-300 focus:border-pink-500"
                      placeholder="Votre prénom"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-900 font-semibold">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="input-field border-2 border-slate-300 focus:border-pink-500"
                      placeholder="votre@email.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="telephone" className="text-slate-900 font-semibold">Téléphone *</Label>
                    <Input
                      id="telephone"
                      name="telephone"
                      type="tel"
                      value={formData.telephone}
                      onChange={handleChange}
                      required
                      className="input-field border-2 border-slate-300 focus:border-pink-500"
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="objectif" className="text-slate-900 font-semibold">Quel est votre objectif ? *</Label>
                  <select
                    id="objectif"
                    name="objectif"
                    value={formData.objectif}
                    onChange={handleChange}
                    required
                    className="input-field w-full border-2 border-slate-300 focus:border-pink-500"
                  >
                    <option value="">Sélectionnez votre objectif</option>
                    {objectives.map(obj => (
                      <option key={obj.id} value={obj.title}>{obj.title}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="creneau" className="text-slate-900 font-semibold">Créneau préféré *</Label>
                  <select
                    id="creneau"
                    name="creneau"
                    value={formData.creneau}
                    onChange={handleChange}
                    required
                    className="input-field w-full border-2 border-slate-300 focus:border-pink-500"
                  >
                    <option value="">Sélectionnez un créneau</option>
                    {creneaux.map((creneau, idx) => (
                      <option key={idx} value={creneau}>{creneau}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-slate-900 font-semibold">Message (optionnel)</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="input-field border-2 border-slate-300 focus:border-pink-500"
                    rows={4}
                    placeholder="Des questions ? Parlez-nous de vous..."
                  />
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full text-xl py-8 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white font-bold shadow-2xl"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    'Envoi en cours...'
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-6 w-6" />
                      Réserver Ma Séance GRATUITE Maintenant
                    </>
                  )}
                </Button>

                <p className="text-center text-sm text-slate-600">
                  🔒 Vos données sont sécurisées et ne seront jamais partagées
                </p>
              </form>
            </Card>

            <div className="grid md:grid-cols-3 gap-6 mt-12">
              <div className="text-center text-white">
                <div className="text-4xl font-bold mb-2">✓</div>
                <p className="font-semibold">Sans engagement</p>
              </div>
              <div className="text-center text-white">
                <div className="text-4xl font-bold mb-2">✓</div>
                <p className="font-semibold">100% Gratuit</p>
              </div>
              <div className="text-center text-white">
                <div className="text-4xl font-bold mb-2">✓</div>
                <p className="font-semibold">Réponse rapide</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="section-title">Questions Fréquentes</h2>
              <p className="section-description mt-4">
                Tout ce que vous devez savoir avant de commencer
              </p>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqItems.map((item) => (
                <AccordionItem 
                  key={item.id} 
                  value={`item-${item.id}`}
                  className="bg-slate-800/60 border border-slate-700 rounded-lg px-6 hover:border-cyan-500/50 transition-colors"
                >
                  <AccordionTrigger className="text-left text-white font-semibold hover:text-cyan-400">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-300 leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="text-center mt-12">
              <p className="text-slate-300 mb-4">Vous avez d'autres questions ?</p>
              <Button onClick={() => scrollToSection('contact')} variant="outline" className="btn-secondary">
                Contactez-nous
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Événements & Challenges */}
      <section id="evenements" className="section-padding bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Calendar className="h-16 w-16 text-cyan-400 mx-auto mb-4" />
            <h2 className="section-title">Événements & Challenges</h2>
            <p className="section-description mt-4">
              Rejoignez nos événements et relevez les défis !
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {upcomingEvents.map((event, index) => (
              <Card key={event.id} className="glass-card hover:border-pink-500/50 transition-all duration-300 animate-scale-in" style={{animationDelay: `${index * 0.15}s`}}>
                <CardContent className="pt-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    {event.icon === 'trophy' && <Trophy className="h-8 w-8 text-white" />}
                    {event.icon === 'door-open' && <DoorOpen className="h-8 w-8 text-white" />}
                    {event.icon === 'apple' && <Apple className="h-8 w-8 text-white" />}
                  </div>
                  <div className="text-center mb-4">
                    <span className="inline-block bg-cyan-500/20 text-cyan-400 px-3 py-1 rounded-full text-sm font-semibold mb-2">
                      {event.type}
                    </span>
                    <h3 className="text-xl font-bold text-white">{event.title}</h3>
                    <p className="text-pink-400 font-semibold text-sm mt-1">{event.date}</p>
                  </div>
                  <p className="text-slate-300 text-center">{event.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-slate-400 mb-4">Les événements sont mis à jour régulièrement</p>
            <Button onClick={() => scrollToSection('contact')} variant="outline" className="btn-secondary">
              Contactez-nous pour Plus d'Infos
            </Button>
          </div>
        </div>
      </section>

      {/* Contact + Google Maps */}
      <section id="contact" className="section-padding bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Venez Nous Rencontrer</h2>
            <p className="section-description mt-4">
              Nous sommes situés au cœur de Bagnols-sur-Cèze
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div className="space-y-6">
              <div className="glass-card p-6">
                <div className="flex items-start space-x-4">
                  <MapPin className="h-6 w-6 text-pink-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-white font-semibold mb-2">Adresse</h3>
                    <p className="text-slate-300">
                      {gymInfo.name}<br />
                      {gymInfo.address}<br />
                      {gymInfo.postalCode} {gymInfo.city}
                    </p>
                  </div>
                </div>
              </div>

              <div className="glass-card p-6">
                <div className="flex items-start space-x-4">
                  <Clock className="h-6 w-6 text-cyan-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-white font-semibold mb-3">Horaires</h3>
                    <div className="space-y-2">
                      <div>
                        <p className="text-cyan-400 font-semibold">Accès salle 24/7</p>
                        <p className="text-slate-300">Ouvert 7j/7 de 6h à 23h</p>
                      </div>
                      <div className="mt-3 pt-3 border-t border-slate-700">
                        <p className="text-pink-400 font-semibold mb-2">Horaires d'accueil</p>
                        <div className="text-slate-300 text-sm space-y-1">
                          <p>• Lundi - Jeudi : 9h - 19h</p>
                          <p>• Vendredi : 9h - 18h</p>
                          <p>• Samedi : 9h - 12h</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="glass-card p-6">
                <div className="flex items-start space-x-4">
                  <Phone className="h-6 w-6 text-pink-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-white font-semibold mb-2">Contact</h3>
                    <p className="text-slate-300">
                      Tél : <a href={`tel:${gymInfo.phone}`} className="text-cyan-400 hover:underline">{gymInfo.phone}</a><br />
                      Email : <a href={`mailto:${gymInfo.email}`} className="text-cyan-400 hover:underline">{gymInfo.email}</a>
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-slate-400">
                📍 Accès simple et stationnement gratuit à proximité
              </p>
            </div>

            <div className="rounded-lg overflow-hidden shadow-2xl h-96 md:h-full">
              <iframe
                src={gymInfo.mapLink}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Localisation Vita Liberté Bagnols-sur-Cèze"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Widget Instagram */}
      <section className="section-padding bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="section-title">Suivez-Nous sur Instagram</h2>
            <p className="section-description mt-4">
              Découvrez notre quotidien et l'ambiance de la salle
            </p>
          </div>

          <div className="max-w-4xl mx-auto text-center">
            <a
              href="https://instagram.com/vitalibertebagnols"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-3 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:scale-105 transition-transform duration-300"
            >
              <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              <span>@vitalibertebagnols</span>
            </a>
            <p className="text-slate-400 mt-6">
              Rejoignez notre communauté de {liveStats.members}+ membres actifs !
            </p>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="section-padding bg-gradient-to-r from-slate-950 via-purple-900 to-slate-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-radial from-pink-500/10 via-transparent to-transparent"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center animate-fade-in-up">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Votre Transformation Commence Aujourd'hui
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              Ne laissez plus vos objectifs au lendemain.<br />
              Rejoignez des centaines de personnes qui ont déjà franchi le pas.
            </p>
            <Button onClick={() => scrollToSection('essai-gratuit')} size="lg" className="btn-cta pulse-animation">
              <Sparkles className="mr-2 h-6 w-6" />
              Réserver Ma Séance GRATUITE
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 border-t border-slate-800 py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src="/assets/logo-vita-liberte.png" alt="Vita Liberté" className="h-8 w-8" />
                <span className="text-lg font-bold text-white">Vita Liberté Bagnols-sur-Cèze</span>
              </div>
              <p className="text-slate-400">
                Votre salle de sport et fitness à Bagnols-sur-Cèze. Musculation, cardio, coaching.
              </p>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Contact</h3>
              <div className="space-y-2 text-slate-400">
                <p className="flex items-center"><MapPin className="h-4 w-4 mr-2" />{gymInfo.address}</p>
                <p className="flex items-center"><Phone className="h-4 w-4 mr-2" />{gymInfo.phone}</p>
                <p className="flex items-center"><Mail className="h-4 w-4 mr-2" />{gymInfo.email}</p>
              </div>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Liens Rapides</h3>
              <div className="space-y-2 text-slate-400">
                <button onClick={() => scrollToSection('essai-gratuit')} className="block hover:text-pink-400 transition-colors">Séance Gratuite</button>
                <button onClick={() => scrollToSection('tarifs')} className="block hover:text-pink-400 transition-colors">Tarifs</button>
                <button onClick={() => scrollToSection('temoignages')} className="block hover:text-pink-400 transition-colors">Avis</button>
                <button onClick={() => scrollToSection('contact')} className="block hover:text-pink-400 transition-colors">Contact</button>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 text-center text-slate-400">
            <p className="mb-2">
              <strong className="text-white">Mots-clés SEO :</strong> Salle de sport Bagnols-sur-Cèze • Musculation Bagnols-sur-Cèze • Fitness Bagnols-sur-Cèze • Coaching sportif Bagnols-sur-Cèze
            </p>
            <p>&copy; 2026 Vita Liberté Bagnols-sur-Cèze. Tous droits réservés.</p>
          </div>
        </div>
      </footer>

      {/* Bouton WhatsApp Flottant */}
      <a
        href="https://wa.me/33672842760?text=Bonjour%20Vita%20Libert%C3%A9%2C%20je%20souhaite%20des%20informations"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-transform duration-300 animate-bounce"
        style={{animationDuration: '2s'}}
      >
        <MessageCircle className="h-8 w-8 text-white" />
      </a>

      {/* Pop-up de sortie */}
      {showExitPopup && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 animate-fade-in-up">
          <Card className="max-w-2xl w-full bg-gradient-to-br from-pink-600 via-purple-600 to-cyan-600 border-none relative">
            <button
              onClick={() => setShowExitPopup(false)}
              className="absolute top-4 right-4 text-white hover:text-slate-200 transition-colors"
            >
              <X className="h-8 w-8" />
            </button>
            <CardContent className="p-12 text-center">
              <Sparkles className="h-16 w-16 text-yellow-300 mx-auto mb-6" />
              <h2 className="text-4xl font-black text-white mb-4">
                ATTENDEZ ! 🎁
              </h2>
              <p className="text-2xl text-white mb-6">
                Ne Partez Pas Sans Votre<br />
                <span className="font-black text-yellow-300">SÉANCE GRATUITE !</span>
              </p>
              <p className="text-xl text-white/90 mb-8">
                Profitez de votre séance découverte 100% offerte.<br />
                Sans engagement. Testez nos équipements et rencontrez nos coachs !
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => {
                    setShowExitPopup(false);
                    scrollToSection('essai-gratuit');
                  }}
                  size="lg"
                  className="bg-white text-purple-600 hover:bg-slate-100 font-bold text-xl px-8 py-6"
                >
                  Je Réserve Ma Séance Gratuite !
                </Button>
                <Button
                  onClick={() => setShowExitPopup(false)}
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-purple-600 font-bold"
                >
                  Non merci, je pars
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
